/**
 * Hook V2 — useApprenantTimeline
 *
 * Phase wiring 3 : branche sur `GET /api/v1/v2/apprenants/:id/timeline`.
 * Source : dérivé Apprenant.statut + Inscriptions.Created_Time (heuristique).
 * Stage history complète à implémenter plus tard (DB tracking).
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../../../lib/api.js';

export type Stage = 'Inscrit' | 'Début' | 'Milieu' | 'Fin' | 'Terminé' | 'Alerte' | 'Abandon';

export interface StageTransition {
  stage: Stage;
  enteredAt: string | null;
  durationDays: number | null;
}

export interface NextAction {
  label: string;
  dueDate: string | null;
  type: 'form' | 'email' | 'rdv' | 'facture' | 'document' | 'autre';
  overdueDays: number | null;
}

export interface ApprenantTimeline {
  apprenantId: string;
  currentStage: Stage;
  daysOnCurrentStage: number;
  history: StageTransition[];
  nextAction: NextAction | null;
}

const MARTINE_MOCK: ApprenantTimeline = {
  apprenantId: 'demo-martine',
  currentStage: 'Milieu',
  daysOnCurrentStage: 18,
  history: [
    { stage: 'Inscrit', enteredAt: '2025-04-12', durationDays: 3 },
    { stage: 'Début', enteredAt: '2025-04-15', durationDays: 25 },
    { stage: 'Milieu', enteredAt: '2025-05-10', durationDays: 18 },
    { stage: 'Fin', enteredAt: null, durationDays: null },
    { stage: 'Terminé', enteredAt: null, durationDays: null },
  ],
  nextAction: {
    label: 'Relancer F4 mi-parcours (non répondu)',
    dueDate: '2026-05-22',
    type: 'form',
    overdueDays: 5,
  },
};

async function fetchTimeline(id: string): Promise<ApprenantTimeline & { source: 'api' | 'mock' }> {
  try {
    const data = await api.get<ApprenantTimeline>(`/api/v1/v2/apprenants/${id}/timeline`);
    return { ...data, source: 'api' };
  } catch (err) {
    if (err instanceof ApiClientError && [401, 404, 501, 0].includes(err.status)) {
      return { ...MARTINE_MOCK, source: 'mock' };
    }
    return { ...MARTINE_MOCK, source: 'mock' };
  }
}

export function useApprenantTimeline(id: string): {
  data: (ApprenantTimeline & { source: 'api' | 'mock' }) | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useQuery({
    queryKey: ['v2', 'apprenant-timeline', id],
    queryFn: () => fetchTimeline(id),
    staleTime: 60_000,
  });
  return { data: data ?? null, isLoading, error: (error as Error | null) ?? null };
}
